import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Search, GripVertical, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ColumnConfig {
  key: string;
  label: string;
  description?: string;
}

interface ConfigureColumnsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableColumns: ColumnConfig[];
  visibleColumns: string[];
  onSave: (visibleColumns: string[]) => void;
}

export function ConfigureColumnsDialog({
  open,
  onOpenChange,
  availableColumns,
  visibleColumns: initialVisibleColumns,
  onSave,
}: ConfigureColumnsDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState<string[]>(initialVisibleColumns);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  const filteredAvailableColumns = useMemo(() => {
    if (!searchQuery) return availableColumns;
    const query = searchQuery.toLowerCase();
    return availableColumns.filter(
      (col) =>
        col.label.toLowerCase().includes(query) ||
        col.key.toLowerCase().includes(query) ||
        col.description?.toLowerCase().includes(query)
    );
  }, [availableColumns, searchQuery]);

  const visibleColumnConfigs = useMemo(() => {
    return visibleColumns
      .map((key) => availableColumns.find((col) => col.key === key))
      .filter((col): col is ColumnConfig => col !== undefined);
  }, [visibleColumns, availableColumns]);

  const handleToggleColumn = (columnKey: string) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnKey)) {
        return prev.filter((key) => key !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedItem(columnKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(columnKey);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();

    if (!draggedItem || draggedItem === targetColumnKey) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    setVisibleColumns((prev) => {
      const newColumns = [...prev];
      const draggedIndex = newColumns.indexOf(draggedItem);
      const targetIndex = newColumns.indexOf(targetColumnKey);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedItem);

      return newColumns;
    });

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleRemoveColumn = (columnKey: string) => {
    setVisibleColumns((prev) => prev.filter((key) => key !== columnKey));
  };

  const handleSave = () => {
    onSave(visibleColumns);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setVisibleColumns(initialVisibleColumns);
    setSearchQuery("");
    onOpenChange(false);
  };

  const handleResetToDefault = () => {
    setVisibleColumns(availableColumns.map((col) => col.key));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">Configure Columns</DialogTitle>
          <DialogDescription>
            Choose which columns to display in the table
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 px-6 min-h-0 overflow-hidden">
          <div className="grid grid-cols-2 gap-6 h-full min-h-0">
            {/* Available Columns - Left Side */}
            <div className="flex flex-col gap-3 h-full min-h-0">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm">Available Columns</h3>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search columns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="flex-1 min-h-0 max-h-[500px] overflow-y-auto border rounded-lg bg-muted/30">
                <div className="p-2 space-y-1">
                  {filteredAvailableColumns.map((column) => {
                    const isVisible = visibleColumns.includes(column.key);
                    return (
                      <div
                        key={column.key}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-md hover:bg-background transition-colors cursor-pointer",
                          isVisible && "bg-background"
                        )}
                        onClick={() => handleToggleColumn(column.key)}
                      >
                        <Checkbox
                          checked={isVisible}
                          onCheckedChange={() => handleToggleColumn(column.key)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{column.label}</div>
                          {column.description && (
                            <div className="text-xs text-muted-foreground">{column.description}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {filteredAvailableColumns.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No columns found
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Visible Columns - Right Side */}
            <div className="flex flex-col gap-3 h-full min-h-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">Visible Columns</h3>
                <Badge variant="secondary" className="text-xs">
                  {visibleColumns.length}
                </Badge>
              </div>

              <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">
                Drag to reorder
              </div>

              <div className="flex-1 min-h-0 max-h-[500px] overflow-y-auto border rounded-lg bg-muted/30">
                <div className="p-2 space-y-1">
                  {visibleColumnConfigs.length > 0 ? (
                    visibleColumnConfigs.map((column) => (
                      <div
                        key={column.key}
                        draggable
                        onDragStart={(e) => handleDragStart(e, column.key)}
                        onDragOver={(e) => handleDragOver(e, column.key)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, column.key)}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-md bg-background hover:bg-accent transition-colors cursor-move group",
                          draggedItem === column.key && "opacity-50",
                          dragOverItem === column.key && "border-2 border-primary"
                        )}
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <Checkbox
                          checked={true}
                          onCheckedChange={() => handleRemoveColumn(column.key)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{column.label}</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveColumn(column.key);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No columns selected
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/30 flex-shrink-0">
          <Button variant="outline" onClick={handleResetToDefault}>
            Reset to Default
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSave}>
              Save Configuration
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
